import { fileExtension, fileFormatKind } from '@shared/lib/file-format';

import { sanitizeUploadFilename } from './service/orchestrator-client';
import { WIRE } from './service/wire';
import type { AttachedFile, InteractionProposal, Message } from './types';

export interface VaultSaveFile {
  key: string;
  name: string;
  size?: number;
}

const VAULT_SAVE_QUESTION = /^Save (.+) to your Data Vault\?$/;
const VAULT_EXTENSIONS = new Set([
  'csv',
  'doc',
  'docx',
  'jpeg',
  'jpg',
  'json',
  'markdown',
  'md',
  'pdf',
  'png',
  'text',
  'txt',
  'xls',
  'xlsx',
]);
const ORCHESTRATOR_FILENAME_LIMIT = 200;

export function precedingUserFiles(messages: Message[], index: number): AttachedFile[] | undefined {
  for (let i = index - 1; i >= 0; i--) {
    const prior = messages[i];
    if (prior?.role === 'user') return prior.files;
  }
  return undefined;
}

export function isProposedByModel(proposal: InteractionProposal): boolean {
  return 'file_name' in proposal.content;
}

function isSlideDeck(name: string): boolean {
  return fileFormatKind(fileExtension(name)) === 'powerpoint';
}

export function orchestratorFilename(name: string): string {
  const safe = sanitizeUploadFilename(name).replace(/[^\p{L}\p{N}_.-]/gu, '_');
  return Array.from(safe).slice(0, ORCHESTRATOR_FILENAME_LIMIT).join('') || 'upload';
}

export function allKeptVaultQuestion(names: string[]): string {
  const subject = names.length === 1 ? 'this file' : 'these files';
  return [
    `Save ${names.join(', ')} to your Data Vault?`,
    `Uploads to this chat are temporary and stop being reachable about a day later; the Vault keeps ${subject}.`,
    `**Approve** saves ${subject} now. Once you approve, it also saves the files I make from ${subject} in this ` +
      'conversation — workbooks, charts, documents and images; slide decks cannot be kept in the Vault. ' +
      '**Reject** carries on with your request and saves nothing.',
  ].join('\n\n');
}

function askedFileNames(plainText: string): string[] | undefined {
  const [question = ''] = plainText.split('\n', 1);
  const names = VAULT_SAVE_QUESTION.exec(question)?.[1]?.split(', ');
  return names && plainText === allKeptVaultQuestion(names) ? names : undefined;
}

function asksAboutExactly(proposal: InteractionProposal, files: AttachedFile[]): boolean {
  const unclaimed = askedFileNames(proposal.plainText);
  if (unclaimed?.length !== files.length) return false;
  return files.every((file) => {
    const index = unclaimed.indexOf(orchestratorFilename(file.name));
    if (index !== -1) unclaimed.splice(index, 1);
    return index !== -1;
  });
}

export function vaultSaveFiles(
  proposal: InteractionProposal,
  relatedFiles: AttachedFile[] | undefined,
): VaultSaveFile[] {
  if (isProposedByModel(proposal)) {
    const { file_name: fileName } = proposal.content;
    const name = typeof fileName === 'string' ? fileName.trim() : '';
    return name && VAULT_EXTENSIONS.has(fileExtension(name)) ? [{ key: name, name }] : [];
  }
  const files = relatedFiles ?? [];
  if (files.some((file) => isSlideDeck(file.name))) return [];
  const askedRefs = askedFileRefs(proposal);
  if (askedRefs) return filesByRef(askedRefs, files);
  if (!asksAboutExactly(proposal, files)) return [];
  return files.map(({ id, name, size }) => ({ key: id, name, size }));
}

function askedFileRefs(proposal: InteractionProposal): string[] | undefined {
  const { files } = proposal.content;
  if (!Array.isArray(files)) return undefined;
  return files.map((entry: unknown) => {
    const ref =
      typeof entry === 'object' && entry !== null
        ? (entry as Record<string, unknown>)[WIRE.fileRef]
        : undefined;
    return typeof ref === 'string' ? ref : '';
  });
}

function filesByRef(refs: string[], files: AttachedFile[]): VaultSaveFile[] {
  const listed: VaultSaveFile[] = [];
  for (const ref of refs) {
    const file = ref ? files.find((candidate) => candidate.fileRef === ref) : undefined;
    if (!file || listed.some((entry) => entry.key === file.id)) return [];
    listed.push({ key: file.id, name: file.name, size: file.size });
  }
  return listed;
}
